import { useState, useCallback } from 'react';
import { X, Upload, TriangleAlert as AlertTriangle, Check, FileSpreadsheet } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import type { Account } from '../../types';

interface Props {
  accounts: Account[];
  onClose: () => void;
  onComplete: () => void;
}

interface ParsedRow {
  first_name: string;
  last_name: string;
  role: string;
  email: string;
  phone: string;
  linkedin_url: string;
  department: string;
  location: string;
  company: string;
  matched_account_id: string | null;
  matched_account_name: string | null;
  skip_reason: string;
}

const MAX_CONTACTS = 100;

function parseCsv(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase().trim());
  return lines.slice(1).map(line => {
    const values = parseCsvLine(line);
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = values[i]?.trim() || ''; });
    return obj;
  });
}

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function findColumn(row: Record<string, string>, ...candidates: string[]): string {
  for (const c of candidates) {
    const key = Object.keys(row).find(k => k.replace(/[_\s-]/g, '').toLowerCase() === c.replace(/[_\s-]/g, '').toLowerCase());
    if (key && row[key]) return row[key];
  }
  return '';
}

function inferSeniority(role: string): string {
  const r = role.toLowerCase();
  if (/\b(ceo|cfo|cto|cio|coo|cmo|cro|chief)\b/.test(r)) return 'executive';
  if (/\b(president|evp|svp|gm|managing director)\b/.test(r)) return 'executive_leadership';
  if (/\b(vp|vice president|head of)\b/.test(r)) return 'senior_leadership';
  if (/\b(director|senior director)\b/.test(r)) return 'department_leader';
  if (/\b(manager|lead|team lead|supervisor)\b/.test(r)) return 'team_manager';
  return 'individual_contributor';
}

function inferDepartment(role: string): string {
  const r = role.toLowerCase();
  if (/\b(engineer|developer|devops|sre|platform|infrastructure|data|ml|ai|architect)\b/.test(r)) return 'Engineering';
  if (/\b(sale|account exec|bdr|sdr|business development|revenue|commercial)\b/.test(r)) return 'Sales';
  if (/\b(market|brand|growth|demand gen|content|comms|communication)\b/.test(r)) return 'Marketing';
  if (/\b(financ|accounti|treasury|controller|audit)\b/.test(r)) return 'Finance';
  if (/\b(human|people|talent|recruit|hr)\b/.test(r)) return 'Human Resources';
  if (/\b(legal|compliance|governance|counsel)\b/.test(r)) return 'Legal';
  if (/\b(product|ux|design)\b/.test(r)) return 'Product';
  if (/\b(operat|supply|logistics|procurement)\b/.test(r)) return 'Operations';
  if (/\b(security|cyber|infosec|ciso)\b/.test(r)) return 'Security';
  if (/\b(support|success|service)\b/.test(r)) return 'Customer Success';
  if (/\b(it |information technology)\b/.test(r)) return 'IT';
  return '';
}

function inferBuyingRole(role: string): string {
  const r = role.toLowerCase();
  if (/\b(ceo|cfo|cto|cio|chief|president|gm)\b/.test(r)) return 'economic_buyer';
  if (/\b(architect|engineer|developer|sre|devops)\b/.test(r)) return 'technical_buyer';
  if (/\b(vp|vice president|director|head of)\b/.test(r)) return 'champion';
  if (/\b(manager|lead|team lead)\b/.test(r)) return 'influencer';
  return 'validator';
}

export default function ContactsCsvUploadModal({ accounts, onClose, onComplete }: Props) {
  const { apiPost } = useApi();
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'done'>('upload');
  const [parsed, setParsed] = useState<ParsedRow[]>([]);
  const [importResult, setImportResult] = useState({ imported: 0, skipped: 0 });

  const accountMap = new Map(accounts.map(a => [a.company_name.toLowerCase().trim(), a]));

  const handleFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const rows = parseCsv(text);

      if (rows.length > MAX_CONTACTS) {
        alert(`Maximum ${MAX_CONTACTS} contacts per upload. Your file has ${rows.length} rows.`);
        return;
      }

      const mapped: ParsedRow[] = rows.map(row => {
        const fullName = findColumn(row, 'name', 'fullname', 'full_name', 'contact_name');
        let firstName = findColumn(row, 'first_name', 'firstname', 'first');
        let lastName = findColumn(row, 'last_name', 'lastname', 'last', 'surname');

        if (!firstName && fullName) {
          const parts = fullName.split(/\s+/);
          firstName = parts[0] || '';
          lastName = parts.slice(1).join(' ') || '';
        }

        const company = findColumn(row, 'company', 'company_name', 'organisation', 'organization', 'account', 'account_name');
        const matched = company ? accountMap.get(company.toLowerCase().trim()) : undefined;

        return {
          first_name: firstName,
          last_name: lastName,
          role: findColumn(row, 'role', 'title', 'job_title', 'jobtitle', 'position'),
          email: findColumn(row, 'email', 'email_address', 'emailaddress'),
          phone: findColumn(row, 'phone', 'phone_number', 'mobile', 'telephone'),
          linkedin_url: findColumn(row, 'linkedin', 'linkedin_url', 'linkedinurl', 'linkedin_profile'),
          department: findColumn(row, 'department', 'dept', 'team'),
          location: findColumn(row, 'location', 'city', 'country', 'region'),
          company,
          matched_account_id: matched?.id || null,
          matched_account_name: matched?.company_name || null,
          skip_reason: !firstName ? 'Missing name' : !company ? 'Missing company' : !matched ? `No matching account: "${company}"` : '',
        };
      });

      setParsed(mapped);
      setStep('preview');
    };
    reader.readAsText(file);
  }, [accountMap]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleImport = async () => {
    setStep('importing');
    const valid = parsed.filter(r => r.matched_account_id && !r.skip_reason);

    // Group valid rows by matched account: the bulk endpoint owns account_id from
    // the path and applies the org-member write gate per account, so each account's
    // rows are posted separately (account_id is never sent in the body).
    const byAccount = new Map<string, ParsedRow[]>();
    for (const r of valid) {
      const group = byAccount.get(r.matched_account_id!);
      if (group) group.push(r);
      else byAccount.set(r.matched_account_id!, [r]);
    }

    let imported = 0;
    for (const [accountId, rows] of byAccount) {
      const contacts = rows.map(r => {
        const seniority = inferSeniority(r.role);
        return {
          first_name: r.first_name,
          last_name: r.last_name,
          role: r.role,
          email: r.email,
          phone: r.phone,
          linkedin_url: r.linkedin_url,
          department: r.department || inferDepartment(r.role),
          location: r.location,
          seniority,
          buying_committee_role: inferBuyingRole(r.role),
          source: 'csv' as const,
          org_level: seniority === 'executive' ? 1 : seniority === 'executive_leadership' ? 2 : 3,
        };
      });

      try {
        const { inserted } = await apiPost<{ inserted: unknown[] }>(
          `/v1/account/${accountId}/contacts/bulk`,
          { contacts },
        );
        imported += inserted?.length ?? 0;
      } catch {
        // Skip this account's batch on error, matching the legacy per-batch behaviour.
      }
    }

    setImportResult({ imported, skipped: parsed.length - valid.length });
    setStep('done');
  };

  const validCount = parsed.filter(r => !r.skip_reason).length;
  const skippedCount = parsed.filter(r => r.skip_reason).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-app-surface rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-app-line">
          <h2 className="text-lg font-bold text-app-ink">Upload Contacts</h2>
          <button onClick={onClose} className="p-2 text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 'upload' && (
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
              className="border-2 border-dashed border-app-line-strong rounded-lg p-12 text-center hover:border-forest-400 transition-colors"
            >
              <Upload className="w-10 h-10 text-app-muted mx-auto mb-3" />
              <p className="text-sm font-medium text-app-ink mb-1">Drag and drop a CSV file here</p>
              <p className="text-xs text-app-muted mb-4">Maximum {MAX_CONTACTS} contacts per upload</p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-forest-600 text-white text-sm font-semibold rounded-lg hover:bg-forest-700 cursor-pointer transition-colors">
                <FileSpreadsheet className="w-4 h-4" />
                Choose File
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
                />
              </label>
              <p className="text-[11px] text-app-muted mt-4">
                Required columns: Name (or First Name + Last Name), Company/Account.
                Optional: Role/Title, Email, Phone, LinkedIn, Department, Location.
              </p>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-app-positive/10 rounded-lg border border-app-positive">
                  <Check className="w-4 h-4 text-app-positive" />
                  <span className="text-sm font-semibold text-app-positive">{validCount} contacts ready</span>
                </div>
                {skippedCount > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-app-warning/10 rounded-lg border border-app-warning">
                    <AlertTriangle className="w-4 h-4 text-app-warning" />
                    <span className="text-sm font-semibold text-app-warning">{skippedCount} will be skipped</span>
                  </div>
                )}
              </div>

              <div className="bg-app-surface border border-app-line rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-app-canvas z-10">
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-app-muted uppercase">Status</th>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-app-muted uppercase">Name</th>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-app-muted uppercase">Company</th>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-app-muted uppercase">Role</th>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-app-muted uppercase">Email</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-app-line">
                      {parsed.map((row, i) => (
                        <tr key={i} className={row.skip_reason ? 'bg-app-critical/10' : ''}>
                          <td className="px-3 py-2">
                            {row.skip_reason ? (
                              <span className="text-[10px] font-medium text-app-critical">{row.skip_reason}</span>
                            ) : (
                              <Check className="w-3.5 h-3.5 text-app-positive" />
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-app-ink">{row.first_name} {row.last_name}</td>
                          <td className="px-3 py-2 text-xs text-app-muted">{row.matched_account_name || row.company}</td>
                          <td className="px-3 py-2 text-xs text-app-muted truncate max-w-[150px]">{row.role}</td>
                          <td className="px-3 py-2 text-xs text-app-muted truncate max-w-[150px]">{row.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-forest-300 border-t-forest-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm font-medium text-app-ink">Importing contacts...</p>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-app-positive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-app-positive" />
              </div>
              <p className="text-lg font-bold text-app-ink">{importResult.imported} contacts imported</p>
              {importResult.skipped > 0 && (
                <p className="text-sm text-app-muted mt-1">{importResult.skipped} rows skipped (no matching account)</p>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-app-line px-6 py-4 flex justify-end gap-3">
          {step === 'preview' && (
            <>
              <button onClick={() => { setParsed([]); setStep('upload'); }} className="px-4 py-2 text-sm text-app-muted hover:text-app-ink">
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={validCount === 0}
                className="px-4 py-2 bg-forest-600 text-white text-sm font-semibold rounded-lg hover:bg-forest-700 disabled:opacity-50 transition-colors"
              >
                Import {validCount} Contacts
              </button>
            </>
          )}
          {step === 'done' && (
            <button
              onClick={() => { onComplete(); onClose(); }}
              className="px-4 py-2 bg-forest-600 text-white text-sm font-semibold rounded-lg hover:bg-forest-700 transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
